import { type AttachmentFile, ContractSuretyType, Dtos, FileType } from "@inrev/common";
import type { SuretyAccount } from "@portal/api/account";
import {
	BondRequest,
	QuoteBondForm,
	useCloseBondRequest,
	useFetchQuoteBondForm,
} from "@portal/api/bond-request/agent";
import { useFetchQuoteAttachments } from "@portal/api/quote";
import { BondRequestProcessingBanner } from "@portal/components/features/banners";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Modal } from "@portal/components/layout/Modal";
import { Button, Card, Icon, Text } from "@portal/components/primitives";
import { MoreActionsMenu } from "@portal/components/ui/MoreActionsMenu";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useState } from "react";
import { match } from "ts-pattern";
import { getNestedValue, getPartialFields } from "../../../../../utils/schema-fields";
import { ClosedReasonModal } from "../../../../shared/account/ClosedReasonModal";
import { AgentBondRequestHeaderNotice } from "../../../../shared/request/AgentBondRequestHeaderNotice";
import { BondRequestLayout } from "../../../../shared/request/BondRequestLayout";
import { AgentActionItemsTable } from "../../../shared/action-items/AgentActionItemsTable";
import type { ActionItem } from "../../../shared/action-items/types";
import { ContractRfpUploadModal } from "./ContractRfpUploadModal";

type ReviewBondRequestViewProps = {
	request: BondRequest;
	account: SuretyAccount;
};

const FALLBACK_REVIEW_REASON = "One or more details of the request needs review";

export const ReviewReasonsCard = ({ request }: { request: BondRequest }) => {
	const reviewReasonDescriptions = Array.from(
		new Set(request.reviewReasons.map((reason) => reason.description)),
	);
	const reasons =
		reviewReasonDescriptions.length > 0 ? reviewReasonDescriptions : [FALLBACK_REVIEW_REASON];
	const supplementalRequirements = Array.from(new Set(request.supplementalRequirements ?? []));

	return (
		<Card
			elevation="raised"
			className="rounded-xl"
			data-testid="agent-bond-request-review-reasons-card"
		>
			<Card.Body className="p-6">
				<div className="flex flex-col w-full space-y-5">
					<Text type="title" color="primary">
						What caused a review?
					</Text>
					<ol className="flex flex-col space-y-2 px-3 list-inside">
						{reasons.map((description) => (
							<li key={description} className="whitespace-pre-wrap">
								<Text type="body" color="primary">
									{description}
								</Text>
							</li>
						))}
					</ol>
					{supplementalRequirements.length > 0 && (
						<div className="flex flex-col space-y-2.5">
							<Text type="body-strong" color="primary">
								Action Required
							</Text>
							<Text type="body" color="tertiary">
								Please ensure the following additional documents are attached:
							</Text>
							<ol className="flex flex-col px-3 space-y-1 list-disc list-inside">
								{supplementalRequirements.map((requirement) => (
									<li key={requirement}>
										<Text type="body" color="primary">
											{requirement}
										</Text>
									</li>
								))}
							</ol>
						</div>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};

export const ReviewBondRequestView = ({ request, account }: ReviewBondRequestViewProps) => {
	const [showClosedReasonModal, setShowClosedReasonModal] = useState(false);
	const { closeBondRequest, closeBondRequestLoading, closeBondRequestError } = useCloseBondRequest(
		request.suretyAccountId,
		request.id,
	);
	const { quoteBondForm, quoteBondFormError } = useFetchQuoteBondForm(
		request.suretyAccountId,
		request.id,
	);
	const { attachments, attachmentsError, attachmentsIsLoading } = useFetchQuoteAttachments(
		request.suretyAccountId,
		request.id,
	);
	const availableAttachments = attachmentsIsLoading || attachmentsError ? undefined : attachments;
	const bondFormPending = quoteBondForm === undefined && !quoteBondFormError;

	return (
		<BondRequestLayout
			request={request}
			account={account}
			banner={<BondRequestProcessingBanner underwritingStatus={request.underwritingStatus} />}
			actionButtons={
				<div className="flex items-center flex-col space-y-[4px] select-none">
					<Button color="primary" variant="solid" disabled className="w-full">
						<div className="flex items-center space-x-[7px]">
							<span>Accept Terms</span>
						</div>
					</Button>
					<MoreActionsMenu
						items={[
							{
								label: "Close Bond Request",
								icon: <Icon type="minus-circle" size="mini" />,
								onClick: () => setShowClosedReasonModal(true),
								trackingId: "bond-request-agent-close",
							},
						]}
						panelClassName="mt-[10px]"
					/>
				</div>
			}
			termsAndConditionsOpen={false}
			headerNotice={
				<AgentBondRequestHeaderNotice
					icon={<Icon type="headset" color="tertiary" size="small" />}
					textClassName="italic"
				>
					An underwriter is taking a closer look at this request and may reach out with additional
					questions.
				</AgentBondRequestHeaderNotice>
			}
		>
			<ReviewReasonsCard request={request} />
			<MissingInformation
				request={request}
				attachments={availableAttachments}
				quoteBondForm={quoteBondForm}
				isLoading={attachmentsIsLoading || bondFormPending}
			/>
			{showClosedReasonModal && (
				<ClosedReasonModal
					closeFunction={closeBondRequest}
					closeFunctionError={closeBondRequestError}
					isClosing={closeBondRequestLoading}
					onDismiss={() => setShowClosedReasonModal(false)}
					closingAction="close"
				/>
			)}
			{closeBondRequestLoading && (
				<Modal>
					<LoadingModal />
				</Modal>
			)}
		</BondRequestLayout>
	);
};

const BondUpdateDto = Dtos.SuretyQuote.Update;
type MissingSection = {
	key: string;
	label: string;
	reason: string;
	sectionId?: string;
};

// The Update schema has city/state flat on project; the submitted request nests them under
// project.address. Without this remap, the lookup below always misses.
const REQUEST_FIELD_PATH_ALIASES: Record<string, string> = {
	"project.city": "project.address.city",
	"project.state": "project.address.state",
};

export const getMissingSections = (
	request: BondRequest,
	attachments: AttachmentFile[] | undefined,
	quoteBondForm?: QuoteBondForm,
): MissingSection[] => {
	const { schema, fileType, documentLabel, documentReason } = match(request.contractSuretyType)
		.with(ContractSuretyType.bid, () => ({
			schema: BondUpdateDto.Bid,
			fileType: FileType.bid_package,
			documentLabel: "RFP / Bid Package",
			documentReason: "No RFP / Bid Package uploaded.",
		}))
		.with(ContractSuretyType.final, () => ({
			schema: BondUpdateDto.Final,
			fileType: FileType.contract,
			documentLabel: "Contract",
			documentReason: "No contract uploaded.",
		}))
		.with(ContractSuretyType.bid_to_final, () => ({
			schema: BondUpdateDto.BidToFinal,
			fileType: FileType.contract,
			documentLabel: "Contract",
			documentReason: "No contract uploaded.",
		}))
		.exhaustive();

	const files = attachments?.filter((attachment) => attachment.file.types.includes(fileType));

	const partialFields = getPartialFields(schema);

	const missingFields = partialFields.filter((fieldPath) => {
		const requestPath = REQUEST_FIELD_PATH_ALIASES[fieldPath] ?? fieldPath;
		const value = getNestedValue(request, requestPath);
		return value === undefined || value === null;
	});

	const sections: MissingSection[] = [];
	if (quoteBondForm && !quoteBondForm.templateId && !quoteBondForm.upload?.files?.length) {
		sections.push({
			key: "bond-form",
			label: "Bond Form",
			reason: "No Bond Form selected or uploaded.",
			sectionId: "bond-form-section",
		});
	}
	if (files?.length === 0) {
		sections.push({
			key: "contract",
			label: documentLabel,
			reason: documentReason,
			sectionId: "contract-section",
		});
	}
	if (missingFields.length > 0) {
		sections.push({
			key: "details",
			label: "Bond Request Details",
			reason: "Bond request details are incomplete.",
			sectionId: "bond-details-section",
		});
	}

	return sections;
};

type MissingAction = { type: "scroll"; sectionId: string } | { type: "upload-contract" };

export const MissingInformation = ({
	request,
	attachments,
	quoteBondForm,
	isLoading = false,
}: {
	request: BondRequest;
	attachments: AttachmentFile[] | undefined;
	quoteBondForm?: QuoteBondForm;
	isLoading?: boolean;
}) => {
	const isContractAiEnabled = useIsMinimalBondUIEnabled();
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const sections = getMissingSections(request, attachments, quoteBondForm);
	if (isLoading || sections.length === 0) return null;

	const rows: ActionItem<MissingAction>[] = sections.map((section) => {
		if (section.key === "contract" && isContractAiEnabled) {
			return {
				key: section.key,
				severity: "warn",
				label: section.label,
				reason: section.reason,
				action: { type: "upload-contract" },
				actionLabel: "Upload",
			};
		}
		return {
			key: section.key,
			severity: "warn",
			label: section.label,
			reason: section.reason,
			action: section.sectionId ? { type: "scroll", sectionId: section.sectionId } : undefined,
			actionLabel: "View section",
		};
	});

	const scrollToSection = (sectionId: string) => {
		const element = document.getElementById(sectionId);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "center" });
		}
	};

	const handleActionClick = (row: ActionItem<MissingAction>) => {
		if (!row.action) return;
		if (row.action.type === "upload-contract") {
			setUploadModalOpen(true);
			return;
		}
		scrollToSection(row.action.sectionId);
	};

	return (
		<Card
			elevation="raised"
			className="rounded-xl"
			data-testid="agent-bond-request-missing-info-card"
		>
			<Card.Body className="rounded-b-none p-6">
				<div className="w-full h-fit min-h-fit flex flex-col space-y-[18px]">
					<Text type="title" color="primary">
						Action Items
					</Text>
					<AgentActionItemsTable rows={rows} onActionClick={handleActionClick} />
					{uploadModalOpen && (
						<ContractRfpUploadModal
							accountId={request.suretyAccountId}
							quoteId={request.id}
							contractSuretyType={request.contractSuretyType}
							onClose={() => setUploadModalOpen(false)}
						/>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};
